import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, tap } from 'rxjs'
import { HomeElem } from 'src/app/shared/models/main/home-elem'
import { Backend } from '../../shared/enums/enums'
import { DdcLoggerService } from '../log/ddc-logger.service'

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private userInfoSubject = new BehaviorSubject<any>(null)
  public userInfo$: Observable<any> = this.userInfoSubject.asObservable()
  private _authCheck$: Observable<any> | null = null

  constructor(
    private http: HttpClient,
    private logger: DdcLoggerService,
  ) {}

  get_user_info(): void {
    this.logger.info('USER_INFO | get_user_info')

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    this.http
      .get(`${Backend.HOST_CONTAINER + Backend.USER_NAVBAR_INFO}`, httpOptions)
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'USER_INFO | get_user_info | catchError | errorMessage: ' + errorMessage,
          )
          return of(null)
        }),
      )
      .subscribe((userInfo: any) => {
        this.userInfoSubject.next(userInfo)
      })
  }

  checkAuth(): Observable<any> {
    if (this._authCheck$) {
      return this._authCheck$
    }
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true,
    }
    this._authCheck$ = this.http
      .get(`${Backend.HOST_CONTAINER + Backend.USER_NAVBAR_INFO}`, httpOptions)
      .pipe(
        tap((userInfo: any) => {
          this.userInfoSubject.next(userInfo)
        }),
        catchError((error: any) => {
          this.logger.error('USER_INFO | checkAuth | error: ' + error.error)
          this.userInfoSubject.next(null)
          // Clear cache on failure so next call retries
          this._authCheck$ = null
          return of(null)
        }),
        shareReplay(1),
      )
    return this._authCheck$
  }

  get_user_profile(): any {
    this.logger.info('USER_PROFILE | get_user_profile')

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http.get(`${Backend.HOST_CONTAINER + Backend.USER_PROFILE}`, httpOptions).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error(
          'USER_PROFILE | get_user_profile | catchError | errorMessage: ' + errorMessage,
        )
        return of(errorMessage)
      }),
    )
  }

  get_public_user_info(username: string): any {
    this.logger.info('PUBLIC_USER_INFO | get_public_user_info | username: ' + username)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    const userData = {
      username: username,
    }

    return this.http
      .post(`${Backend.HOST_CONTAINER + Backend.PUBLIC_USER_INFO}`, userData, httpOptions)
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'PUBLIC_USER_INFO | get_public_user_info | catchError | errorMessage: ' + errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }

  update_user_profile(credentials: any): any {
    this.logger.debug('UPDATE_USER_PROFILE | credentials: ' + JSON.stringify(credentials))

    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        // Ensure the following line sets the correct content type
        // 'Content-Type': 'multipart/form-data',
      }),
      withCredentials: true,
    }

    const userData = {
      // username: credentials.username,
      // email: credentials.email,
      bio: credentials.bio,
      country: credentials.country,
      city: credentials.city,
      favLanguage1: credentials.favLanguage1,
      favLanguage2: credentials.favLanguage2,
      favLanguage3: credentials.favLanguage3,
    }

    return this.http
      .post(`${Backend.HOST_CONTAINER + Backend.USER_PROFILE}`, userData, httpOptions)
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('UPDATE_USER_PROFILE | catchError | errorMessage: ' + errorMessage)
          return of(errorMessage)
        }),
      )
  }

  update_user_profile_pic(selected_file: any): any {
    this.logger.info('UPDATE_USER_PROFILE_PIC | selected_file: ' + selected_file)

    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        // Ensure the following line sets the correct content type
        // 'Content-Type': 'multipart/form-data',
      }),
      withCredentials: true,
    }

    const formData = new FormData()
    formData.append('profile_pic', selected_file, selected_file.name)

    return this.http
      .post(`${Backend.HOST_CONTAINER + Backend.UPDATE_USER_PROFILE_PIC}`, formData, httpOptions)
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('UPDATE_USER_PROFILE_PIC | catchError | errorMessage: ' + errorMessage)
          return of(errorMessage)
        }),
      )
  }
  get_user_home(): any {
    this.logger.info('USER_HOME')

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http.get(`${Backend.HOST_CONTAINER + Backend.USER_HOME_ITEMS}`, httpOptions).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: any) => {
        const errorMessage = error.error
        this.logger.error('USER_HOME | catchError | errorMessage: ' + errorMessage)
        return of(errorMessage)
      }),
    )
  }

  get_user_home_page(
    page: number,
    pageSize: number,
  ): Observable<{ results: HomeElem[]; count: number }> {
    this.logger.info('USER_HOME | get_user_home_page | page: ' + page + ' | pageSize: ' + pageSize)

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    return this.http
      .get<{
        results: HomeElem[]
        count: number
      }>(
        `${Backend.HOST_CONTAINER + Backend.USER_HOME_ITEMS}?page=${page}&pageSize=${pageSize}`,
        httpOptions,
      )
      .pipe(
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('get_user_home_page | catchError | errorMessage: ' + errorMessage)
          throw error // Re-throw to allow proper error handling
        }),
      )
  }

  get_searched_users(target: string, maxRows: number = 10): any {
    this.logger.info(
      'USER/Search | get_searched_users | target: ' + target + ' | maxRows: ' + maxRows,
    )

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    }

    // limit max rows to 10
    if (maxRows > 10 || maxRows < 1) maxRows = 10

    return this.http
      .get(
        `${Backend.HOST_CONTAINER + Backend.RELATIONSHIP_SEARCH}?max=${maxRows}&user=${target}`,
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'USER/Search | get_searched_users | catchError | errorMessage: ' + errorMessage,
          )
          return of(errorMessage)
        }),
      )
  }
}
